/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import _ from 'lodash'
import moment from 'moment'

export interface HighlightSegment {
  /** Plain text. Never markup -- see `highlightSegments`. */
  text: string
  /** Whether this run matched the search query and should be marked. */
  match: boolean
}

interface Range {
  start: number
  end: number
}

/**
 * Split `word` into matched and unmatched runs for the search highlighter.
 *
 * Returns **data, never markup**. This function used to build an HTML string --
 * it spliced `<mark>` tags around the matches and the templates handed the
 * result to `v-html` -- so every value it touched was parsed as HTML by the
 * browser. A DNS server name or an email address holding
 * `<img src=x onerror=...>` therefore ran as script in an administrator's
 * session (findings 3.1 and 3.14).
 *
 * Escaping the string before splicing would have closed that too, but it leaves
 * the next person one `v-html` away from reopening it. Returning segments
 * removes the question instead: nothing on this path is HTML, and
 * `HighlightedText.vue` renders each run as text.
 *
 * Note that the old version needed no search term to be dangerous -- with an
 * empty query it returned the stored value *unchanged*, straight into `v-html`.
 */
export const highlightSegments = function(
  word: any,
  query: any
): HighlightSegment[] {
  const text = word === null || word === undefined ? '' : String(word)
  if (!text) return []

  const terms: any[] = _.isArray(query) ? query : [query]
  const patterns = terms
    .filter(term => _.isString(term) && term.length > 0)
    // Escaped, because the query is whatever an administrator typed into the
    // search box. Compiled raw -- as it was -- a stray '(' threw a SyntaxError
    // that took the whole list with it, and a term like `(a+)+b` is a ReDoS
    // against the browser rendering it.
    .map(term => new RegExp(_.escapeRegExp(_.deburr(term)), 'gimu'))

  if (patterns.length === 0) return [{ text, match: false }]

  // Matching is accent-insensitive, so it runs against the deburred string --
  // but the offsets it produces are used to slice the *original*. `deburr` can
  // change length ('ae' for a single character), which slides every offset
  // after it and cuts the string in the wrong place. Where it does, match
  // against the original: accent-insensitivity is a nicety, showing a mangled
  // server name is a bug.
  const deburred = _.deburr(text)
  const haystack = deburred.length === text.length ? deburred : text

  const ranges: Range[] = []
  patterns.forEach(pattern => {
    const found = [...haystack.matchAll(pattern)]
    found.forEach(match => {
      if (!match[0]) return
      const start = match.index ?? 0
      ranges.push({ start, end: start + match[0].length })
    })
  })

  return toSegments(text, mergeRanges(ranges))
}

/**
 * Overlapping matches, folded into one run each.
 *
 * Two search terms can match overlapping text -- 'ad' and 'min' in
 * 'admin@example.com'. Left separate, the walk below would emit the overlap
 * twice and the address on screen would no longer be the address stored.
 */
function mergeRanges(ranges: Range[]): Range[] {
  const merged: Range[] = []

  _.sortBy(ranges, ['start', 'end']).forEach(range => {
    const last = merged[merged.length - 1]
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end)
    } else {
      merged.push({ ...range })
    }
  })

  return merged
}

/** The text, cut into alternating unmatched and matched runs. */
function toSegments(text: string, ranges: Range[]): HighlightSegment[] {
  const segments: HighlightSegment[] = []
  let cursor = 0

  ranges.forEach(range => {
    if (range.start > cursor) {
      segments.push({ text: text.slice(cursor, range.start), match: false })
    }
    segments.push({ text: text.slice(range.start, range.end), match: true })
    cursor = range.end
  })

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false })
  }

  return segments
}

export const fixDateFormat = function(date: any, format = 'YYYY/MM/DD') {
  return moment(date).format(format)
}

export const readableDiff = function(date: any) {
  return moment.utc(date).fromNow()
}

export const utcFormat = function(date: any, format = 'DD-MM-YYYY, HH:mm') {
  const time = moment.utc(date).toDate()
  return moment(time).format(format)
}

/**
 * Iraq is a fixed UTC+3: it abolished daylight saving, so an offset is exact and
 * needs no timezone database. The portal shows scan times in this zone whatever
 * the agent's browser is set to, because the question it answers is "what time
 * was it for the customer".
 */
export const DISPLAY_UTC_OFFSET_MINUTES = 180

/**
 * A UTC instant, shown in Iraq time. Reads a zone-less server string
 * ("2026-09-09 10:00:00", as the SDK's `start`) as UTC too.
 */
export const iraqTime = function(utc: any, format = 'DD-MM-YYYY, HH:mm') {
  return moment
    .utc(utc)
    .utcOffset(DISPLAY_UTC_OFFSET_MINUTES)
    .format(format)
}

/**
 * When a report was made. Prefers `created_at_utc`, the true UTC instant the
 * backend derives from the report's time-based id. Falls back to the old
 * `created_at` -- the server's local time with no zone, which `utcFormat` can
 * only guess at -- so the portal keeps working against a backend that has not
 * been upgraded yet.
 */
export const reportTime = function(
  report: { created_at?: string; created_at_utc?: string },
  format = 'DD-MM-YYYY, HH:mm'
) {
  if (report.created_at_utc) return iraqTime(report.created_at_utc, format)
  if (report.created_at) return utcFormat(report.created_at, format)
  return ''
}

/** One scan takes a few minutes; anything longer is a bad duration, not a slow scan. */
const MAX_PLAUSIBLE_SCAN_SECONDS = 30 * 60

/**
 * When the scan started, in Iraq time.
 *
 * Derived as the report time minus the scan's duration rather than read from the
 * SDK's `start`, which both SDKs sent on a 12-hour clock with no AM/PM (15:00 UTC
 * arrived as "03:00:00") and which older iOS builds sent in device-local time.
 * The report time is exact and so correct for every report already stored.
 *
 * Falls back to the stored `start` when the duration is missing or implausible
 * (iOS summed durations across scans on a reused manager) or the backend has not
 * sent a UTC time yet.
 */
export const scanStartTime = function(
  createdAtUtc: string | undefined,
  durationSeconds: any,
  storedStart: any,
  format = 'DD-MM-YYYY, HH:mm'
) {
  const duration = Number(durationSeconds)
  if (createdAtUtc && duration > 0 && duration <= MAX_PLAUSIBLE_SCAN_SECONDS) {
    const start = moment.utc(createdAtUtc).subtract(duration, 'seconds')
    return iraqTime(start.toISOString(), format)
  }
  return storedStart ? iraqTime(storedStart, format) : ''
}
